// ════════════════════════════════════════════════════════════
// /admin — Sprint 6.5 (gestion utilisateurs, DAF only)
// ════════════════════════════════════════════════════════════
// Reprise fidèle V1 : liste des users + réinitialisation du mot
// de passe à un mot de passe par défaut connu du DAF.
// Création de users / changement de rôle = Dashboard Supabase.
// ════════════════════════════════════════════════════════════
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  KeyRound,
  Eye,
  EyeOff,
  RotateCcw,
  ShieldCheck,
  History,
  AlertTriangle,
  Check,
  X,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { ROLE_LABELS, ROLE_SHORT_LABELS, type AppRole } from '@/types/auth';
import {
  useAdminUsers,
  useResetUserPassword,
  useSetUserDefaultPassword,
  useAdminAuditLog,
  type AdminAuditEntry,
} from '@/queries/admin';
import type { AdminUser } from '@/lib/adminApi';

export const Route = createFileRoute('/_authed/admin/')({
  component: AdminPage,
});

function roleLabel(role: string | null): string {
  if (role && role in ROLE_LABELS) return ROLE_LABELS[role as AppRole];
  return role ?? '—';
}
function roleShort(role: string | null): string {
  if (role && role in ROLE_SHORT_LABELS) return ROLE_SHORT_LABELS[role as AppRole];
  return role ?? '—';
}

function displayName(u: AdminUser): string {
  const fn = u.fullName?.trim();
  if (fn) return fn;
  const local = u.email?.split('@')[0]?.trim();
  if (local) return local;
  return 'Sans nom';
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'Jamais';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function AdminPage() {
  const { data: users = [], isLoading, error } = useAdminUsers();

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-lg bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
          <ShieldCheck className="size-5" />
        </span>
        <div>
          <h1 className="text-foreground text-xl font-semibold sm:text-2xl">Administration</h1>
          <p className="text-muted-foreground text-sm">
            Gestion des utilisateurs · réinitialisation des mots de passe
          </p>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted h-28 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="text-destructive py-8 text-center text-sm">
            Erreur de chargement : {error instanceof Error ? error.message : 'inconnue'}
          </CardContent>
        </Card>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground py-10 text-center text-sm">
            Aucun utilisateur. Crée-les depuis le Dashboard Supabase.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {users.map((u) => (
            <AdminUserCard key={u.userId} user={u} />
          ))}
        </div>
      )}

      <AdminAuditHistory />

      {/* Footer info (style V1) */}
      <Card className="bg-muted/40">
        <CardContent className="text-muted-foreground space-y-2 py-4 text-xs leading-relaxed">
          <p className="text-foreground font-medium">ℹ️ Comment ça marche</p>
          <p>
            Définis un <strong>mot de passe par défaut</strong> pour chaque personne. Le bouton{' '}
            <strong>Réinitialiser</strong> remet son mot de passe à ce défaut — utile si elle l’a
            oublié. Tu peux ensuite le lui communiquer.
          </p>
          <p>
            La <strong>création</strong> d’un compte et le <strong>changement de rôle</strong> se
            font depuis le Dashboard Supabase (peu fréquent, équipe figée).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const ROLE_BADGE = 'bg-primary/10 text-primary';

function AdminUserCard({ user }: { user: AdminUser }) {
  const [reveal, setReveal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pwd, setPwd] = useState('');

  const resetMut = useResetUserPassword();
  const setMut = useSetUserDefaultPassword();

  const resetting = resetMut.isPending && resetMut.variables === user.userId;
  const saving = setMut.isPending && setMut.variables?.userId === user.userId;

  const name = displayName(user);
  const initial = name.charAt(0).toUpperCase();
  const hasDefault = Boolean(user.defaultPassword);

  const handleReset = () => {
    resetMut.mutate(user.userId, {
      onSuccess: ({ defaultPassword }) => {
        setConfirming(false);
        toast.success(`Mot de passe réinitialisé · ${defaultPassword}`);
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec de la réinitialisation'),
    });
  };

  const handleSave = () => {
    if (pwd.trim().length < 6) {
      toast.error('Au moins 6 caractères');
      return;
    }
    setMut.mutate(
      { userId: user.userId, password: pwd.trim() },
      {
        onSuccess: () => {
          setEditing(false);
          setPwd('');
          toast.success('Mot de passe par défaut enregistré');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Échec de l'enregistrement"),
      },
    );
  };

  return (
    <Card>
      <CardContent className="space-y-3 py-3.5">
        {/* En-tête user */}
        <div className="flex items-center gap-3">
          <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-bold">
            {initial}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-foreground truncate text-sm font-semibold">{name}</span>
              <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', ROLE_BADGE)}>
                {roleShort(user.role)}
              </span>
              {user.banned && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                  Désactivé
                </span>
              )}
            </div>
            <p className="text-muted-foreground truncate font-mono text-xs">{user.email ?? '—'}</p>
          </div>
        </div>

        <p className="text-muted-foreground text-[11px]">
          {roleLabel(user.role)} · dernière connexion : {fmtDate(user.lastSignInAt)}
        </p>

        {/* Bloc mot de passe */}
        <div className="bg-muted/50 space-y-2 rounded-lg border p-3">
          <div className="text-muted-foreground flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide">
            <KeyRound className="size-3" />
            Mot de passe par défaut
          </div>

          {editing ? (
            <div className="space-y-2">
              <Input
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Nouveau mot de passe (min. 6)"
                className="font-mono"
                autoComplete="new-password"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setEditing(false);
                    setPwd('');
                  }}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  className="flex-[2] gap-1.5"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Check className="size-4" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          ) : hasDefault ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <code className="text-foreground text-sm">
                  {reveal ? user.defaultPassword : '••••••••'}
                </code>
                <button
                  type="button"
                  onClick={() => setReveal((v) => !v)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={reveal ? 'Masquer' : 'Afficher'}
                >
                  {reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>

              {confirming ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <span>
                      Réinitialiser le mot de passe de <strong>{name}</strong> ? Il redeviendra{' '}
                      <code className="font-semibold">{user.defaultPassword}</code>.
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={() => setConfirming(false)}
                      disabled={resetting}
                    >
                      <X className="size-4" />
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-[2] gap-1.5"
                      onClick={handleReset}
                      disabled={resetting}
                    >
                      {resetting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <RotateCcw className="size-4" />
                      )}
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditing(true)}
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex-[2] gap-1.5"
                    onClick={() => setConfirming(true)}
                  >
                    <RotateCcw className="size-4" />
                    Réinitialiser
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>Aucun mot de passe par défaut défini.</span>
              </div>
              <Button size="sm" className="w-full gap-1.5" onClick={() => setEditing(true)}>
                <KeyRound className="size-4" />
                Définir le mot de passe par défaut
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const ADMIN_ACTION_LABELS: Record<string, string> = {
  reset_password: 'Mot de passe réinitialisé',
  set_default_password: 'Mot de passe par défaut défini',
};

function AdminAuditHistory() {
  const [open, setOpen] = useState(false);
  const { data: entries = [] } = useAdminAuditLog(30);

  return (
    <Card>
      <CardContent className="py-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-foreground flex w-full items-center gap-2 py-3.5 text-sm font-medium"
        >
          <History className="text-muted-foreground size-4" />
          Historique des actions admin
          {entries.length > 0 && (
            <span className="bg-muted text-muted-foreground rounded-full px-1.5 text-xs">
              {entries.length}
            </span>
          )}
          <ChevronRight
            className={cn(
              'text-muted-foreground ml-auto size-4 transition-transform',
              open && 'rotate-90',
            )}
          />
        </button>

        {open && (
          <div className="border-border space-y-1.5 border-t py-3">
            {entries.length === 0 ? (
              <p className="text-muted-foreground py-2 text-center text-xs">Aucune action.</p>
            ) : (
              entries.map((e: AdminAuditEntry) => (
                <div key={e.id} className="text-xs">
                  <span className="text-foreground font-medium">
                    {ADMIN_ACTION_LABELS[e.action] ?? e.action}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    · {e.targetEmail?.split('@')[0] ?? '—'} · par {e.actorEmail.split('@')[0]} ·{' '}
                    {fmtDate(e.createdAt)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
